import { useState, useEffect, useCallback } from "react";
import { T, F, Over, Pill, Btn } from "@/lib/bricks";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

// TRESA s.27(4)-(7) + O. Reg. 567/05 s.39: two-year clock on money held per deal, locate
// efforts, and the record of paying RECO. Bricks does not pay RECO or contact anyone.
const cad = (cents) => new Intl.NumberFormat('en-CA', { style:'currency', currency:'CAD' }).format((cents || 0) / 100);
const day = (d) => d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('en-CA', { month:'short', day:'numeric', year:'numeric' }) : '—';
const BUCKET = { due:['brick','Two years reached'], due_soon:['warn','Due within 90 days'], '1_to_2_years':['neutral','1–2 years'], under_1_year:['neutral','Under 1 year'], unknown:['neutral','No receipt date'] };
const METHODS = [['phone','Phone'],['email','Email'],['mail','Mail'],['registered_mail','Registered mail'],['skip_trace','Skip trace / search'],['lawyer','Through a lawyer'],['other','Other']];

export default function TrustUnclaimed({ BASE, headers, p, dark, canAuth }) {
  const [data, setData] = useState(null);
  const [caseFor, setCaseFor] = useState(null);
  const [effortFor, setEffortFor] = useState(null);
  const [remitFor, setRemitFor] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    fetch(`${BASE}/api/accounting/trust/unclaimed`, { headers }).then(r => r.ok ? r.json() : null).then(setData).catch(() => setData(null));
  }, [BASE, headers.Authorization]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const send = async (method, path, body, ok) => {
    setBusy(true);
    try {
      const res = await fetch(`${BASE}/api/accounting/trust/unclaimed${path}`, { method, headers, body: JSON.stringify(body || {}) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'That didn’t go through');
      toast.success(ok);
      load();
      return d;
    } catch (e) { toast.error(e.message); return null; }
    finally { setBusy(false); }
  };
  const exportCsv = async () => {
    const res = await fetch(`${BASE}/api/accounting/trust/unclaimed/export.csv`, { headers });
    if (!res.ok) { toast.error('Export failed'); return; }
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a'); a.href = url; a.download = `trust-unclaimed-aging-${data?.as_of || 'export'}.csv`; a.click();
  };

  if (!data) return null;
  const items = data.items || [];
  const canManage = !!data.can_manage;
  const input = { marginTop:8, width:'100%', padding:'10px 12px', border:`1px solid ${p.hairS}`, borderRadius:2, fontSize:14, fontFamily:F.display, color:p.ink, background:p.bg, outline:'none', boxSizing:'border-box' };
  const small = (color) => ({ background:'none', border:`1px solid ${p.hair}`, borderRadius:2, padding:'4px 10px', cursor:'pointer', fontSize:11, fontFamily:F.mono, color });
  const box = { maxWidth:480, borderRadius:2, background:p.bg, border:`1px solid ${p.hair}`, padding:'var(--bx-pad)' };
  const title = { fontFamily:F.display, fontSize:18, fontWeight:500, color:p.ink };
  const desc = { fontFamily:F.display, fontSize:13, color:p.ink3, marginTop:4 };
  const a = data.annual || {};

  return (
    <div style={{ border:`1px solid ${p.hair}`, borderRadius:2 }}>
      <div style={{ padding:'16px var(--bx-pad)', borderBottom:`1px solid ${p.hair}`, display:'flex', justifyContent:'space-between', alignItems:'center', gap:12, flexWrap:'wrap' }}>
        <div>
          <Over color={p.ink3}>Unclaimed money · TRESA s.27(4)–(7)</Over>
          <div style={{ fontSize:13, color:p.ink2, marginTop:4 }}>
            {data.due_count ? `${data.due_count} deal${data.due_count === 1 ? '' : 's'} past two years` : 'Nothing past two years'}
            {data.due_soon_count ? ` · ${data.due_soon_count} due within 90 days` : ''} · {data.honest_note}
          </div>
        </div>
        <Btn variant="ghost" dark={dark} onClick={exportCsv}>Export CSV</Btn>
      </div>
      <div style={{ padding:'10px var(--bx-pad)', borderBottom:`1px solid ${p.hair}`, fontSize:12, color:p.ink3, display:'flex', gap:24, flexWrap:'wrap' }}>
        <span>Year ending {day(a.fiscal_year_end)}: held over two years <span style={{ fontFamily:F.mono, color:p.ink }}>{cad(a.held_over_two_years_cents)}</span> ({a.held_over_two_years_count || 0})</span>
        <span>Paid to RECO in year <span style={{ fontFamily:F.mono, color:p.ink }}>{cad(a.remitted_in_year_cents)}</span> ({a.remitted_in_year_count || 0})</span>
        <span>Under $25 is optional (O. Reg. 567/05 s.39)</span>
      </div>
      {items.length === 0 ? (
        <div style={{ padding:'24px var(--bx-pad)', fontSize:13, color:p.ink3 }}>No money held in trust on any deal.</div>
      ) : items.slice(0, 30).map((i, idx) => {
        const [tone, label] = i.status === 'remitted' ? ['ok', 'Paid to RECO'] : (BUCKET[i.bucket] || ['neutral', i.bucket]);
        return (
          <div key={i.transaction_id} style={{ padding:'12px var(--bx-pad)', borderBottom: idx === Math.min(items.length, 30) - 1 ? 'none' : `1px solid ${p.hair}`, fontSize:13 }}>
            <div style={{ display:'grid', gridTemplateColumns:'1.6fr 1fr 1.2fr 1fr auto', gap:12, alignItems:'center' }}>
              <div>
                <div style={{ color:p.ink }}>{i.property_address || 'Deal'}</div>
                <div style={{ fontSize:11, color:p.ink3, marginTop:2 }}>{i.basis_label}{i.entitled_party ? ` · ${i.entitled_party}` : ''}</div>
              </div>
              <div style={{ fontFamily:F.mono, color:p.ink }}>{i.status === 'remitted' ? cad(i.remitted_cents || 0) : cad(i.balance_cents)}{i.under_25 ? <span style={{ fontSize:10, color:p.ink3 }}> · under $25</span> : null}</div>
              <div style={{ fontSize:12, color:p.ink2 }}>
                {i.status === 'remitted' ? `Paid ${day(i.remitted_at)} · ${i.reco_reference || ''}` : `Clock from ${day(i.clock_start)} · due ${day(i.due_date)}`}
                <div style={{ fontSize:11, color:p.ink3, marginTop:2 }}>{i.efforts_count} locate effort{i.efforts_count === 1 ? '' : 's'}</div>
              </div>
              <div><Pill tone={tone} dark={dark}>{label}</Pill></div>
              <div style={{ display:'flex', gap:6, justifyContent:'flex-end', flexWrap:'wrap' }}>
                {canManage && i.status !== 'remitted' && (<>
                  <button disabled={busy} style={small(p.ink2)} onClick={() => { setCaseFor(i); setForm({ basis: i.basis, entitled_party: i.entitled_party || '', entitlement_date: i.entitlement_date || '', notes: '' }); }}>Basis</button>
                  <button disabled={busy} style={small(T.brick)} onClick={() => { setEffortFor(i); setForm({ method:'phone', outcome:'', effort_date:'' }); }}>Log effort</button>
                </>)}
                {canAuth && i.status !== 'remitted' && i.bucket === 'due' && (
                  <button disabled={busy} style={small(T.brick)} onClick={() => { setRemitFor(i); setForm({ entry_id: i.reco_payment_entry_id || '', reco_reference:'', entitlement_info:'' }); }}>Record RECO payment</button>
                )}
              </div>
            </div>
            {i.status !== 'remitted' && (i.notes || []).length > 0 && (
              <div style={{ fontSize:11, color:p.ink3, marginTop:6 }}>{i.notes.join(' ')}</div>
            )}
          </div>
        );
      })}

      <Dialog open={!!caseFor} onOpenChange={(o) => !o && setCaseFor(null)}>
        <DialogContent style={box}>
          <DialogHeader>
            <DialogTitle style={title}>Why is this money unclaimed?</DialogTitle>
            <DialogDescription style={desc}>Unclear entitlement runs two years from when the money came in (s.27(4)). If you know who is entitled but can't find them, it runs two years from the date they became entitled (s.27(5)).</DialogDescription>
          </DialogHeader>
          <div style={{ display:'flex', flexDirection:'column', gap:14, paddingTop:18 }}>
            <div><Over color={p.ink3}>Basis</Over>
              <select style={input} value={form.basis || 'entitlement_unclear'} onChange={e => setForm({ ...form, basis: e.target.value })}>
                <option value="entitlement_unclear">Entitlement unclear or not determined</option>
                <option value="entitled_unlocated">Entitled person can't be located</option>
              </select>
            </div>
            {form.basis === 'entitled_unlocated' && (<>
              <div><Over color={p.ink3}>Person entitled</Over><input style={input} value={form.entitled_party || ''} onChange={e => setForm({ ...form, entitled_party: e.target.value })}/></div>
              <div><Over color={p.ink3}>Date they became entitled</Over><input type="date" style={input} value={form.entitlement_date || ''} onChange={e => setForm({ ...form, entitlement_date: e.target.value })}/></div>
            </>)}
            <div><Over color={p.ink3}>Notes (optional)</Over><input style={input} value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })}/></div>
          </div>
          <DialogFooter style={{ marginTop:22, gap:10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setCaseFor(null)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} disabled={busy} onClick={async () => { if (await send('PUT', `/${caseFor.transaction_id}`, form, 'Saved')) setCaseFor(null); }}>Save</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!effortFor} onOpenChange={(o) => !o && setEffortFor(null)}>
        <DialogContent style={box}>
          <DialogHeader>
            <DialogTitle style={title}>Log an effort to locate them</DialogTitle>
            <DialogDescription style={desc}>Reasonable efforts are required before paying RECO (s.27(6)). Bricks keeps the log; it doesn't contact anyone.</DialogDescription>
          </DialogHeader>
          <div style={{ display:'flex', flexDirection:'column', gap:14, paddingTop:18 }}>
            <div><Over color={p.ink3}>How</Over>
              <select style={input} value={form.method || 'phone'} onChange={e => setForm({ ...form, method: e.target.value })}>
                {METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div><Over color={p.ink3}>What happened</Over><input style={input} value={form.outcome || ''} onChange={e => setForm({ ...form, outcome: e.target.value })} placeholder="e.g. Registered letter returned, moved"/></div>
            <div><Over color={p.ink3}>Date (blank = today)</Over><input type="date" style={input} value={form.effort_date || ''} onChange={e => setForm({ ...form, effort_date: e.target.value })}/></div>
          </div>
          <DialogFooter style={{ marginTop:22, gap:10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setEffortFor(null)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} disabled={busy || !(form.outcome || '').trim()} onClick={async () => { if (await send('POST', `/${effortFor.transaction_id}/efforts`, form, 'Effort logged')) setEffortFor(null); }}>Log</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!remitFor} onOpenChange={(o) => !o && setRemitFor(null)}>
        <DialogContent style={box}>
          <DialogHeader>
            <DialogTitle style={title}>Record payment to RECO</DialogTitle>
            <DialogDescription style={desc}>Post and approve an “Unclaimed money paid to RECO” trust entry on this deal first, then record RECO's reference and what entitlement information you gave them (s.27(7)). This record is permanent.</DialogDescription>
          </DialogHeader>
          <div style={{ display:'flex', flexDirection:'column', gap:14, paddingTop:18 }}>
            <div><Over color={p.ink3}>Approved payment entry id</Over><input style={{ ...input, fontFamily:F.mono }} value={form.entry_id || ''} onChange={e => setForm({ ...form, entry_id: e.target.value })} placeholder="uuid of the approved unclaimed_to_reco entry"/></div>
            <div><Over color={p.ink3}>RECO reference</Over><input style={input} value={form.reco_reference || ''} onChange={e => setForm({ ...form, reco_reference: e.target.value })}/></div>
            <div><Over color={p.ink3}>Entitlement information given to RECO</Over><input style={input} value={form.entitlement_info || ''} onChange={e => setForm({ ...form, entitlement_info: e.target.value })} placeholder="Names, last known addresses, deal details"/></div>
          </div>
          <DialogFooter style={{ marginTop:22, gap:10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setRemitFor(null)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} disabled={busy || !(form.entry_id || '').trim()} onClick={async () => { if (await send('POST', `/${remitFor.transaction_id}/remit`, form, 'RECO payment recorded')) setRemitFor(null); }}>Record</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
